import {AdvancedAnalysisFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {STEP_TYPE_TO_REQUIRED_CAPABILITIES} from 'graphql-client/__generated/GeneratedEnumMaps';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';

/**
 * Whether the user has access to a pipeline capability.
 *
 * Do not rename this constant, since it is updated by `generate_pipeline_step`.
 *
 * @package
 */
export const PIPELINE_CAPABILITY_CHECK = {
  ADVANCED_ANALYSIS_CREATE_STATISTICS: () =>
    isFeatureEnabled({
      feature: AdvancedAnalysisFeature.ADVANCED_ANALYSIS_CREATE_STATISTICS,
    }),
  ADVANCED_ANALYSIS_CREATE_TEMPLATES: () =>
    isFeatureEnabled({
      feature: AdvancedAnalysisFeature.ADVANCED_ANALYSIS_CREATE_TEMPLATES,
    }),
  AI_DATA_ENTRY: () => BenchlingConfig.ELN_ENABLE_AI_DATA_ENTRY,
  CONNECT: () => BenchlingConfig.INSTRUMENTS_ENABLE,
  CREATE_PROCEDURE_DATASET: () => BenchlingConfig.STUDIES_ENABLE_ANALYSIS_DATA_REFRESH,
  CUSTOM_CODE: () => BenchlingConfig.PIPELINES_ENABLE_CUSTOM_CODE,
  ENGINEERING_ONLY: () => false,
} satisfies Record<string, () => boolean>;

type PipelineCapability = keyof typeof PIPELINE_CAPABILITY_CHECK;

export function isPipelineCapabilityEnabled(pipelineCapability: PipelineCapability): boolean {
  return PIPELINE_CAPABILITY_CHECK[pipelineCapability]?.() ?? false;
}

/**
 * Check if a step type is enabled for a user, based on its required pipeline capabilities.
 */
export default function isStepTypeEnabledForUser(stepType: PipelineStepType): boolean {
  const pipelineCapabilities = STEP_TYPE_TO_REQUIRED_CAPABILITIES[stepType] as Array<PipelineCapability>;
  return pipelineCapabilities.every((pipelineCapabiltiy) => isPipelineCapabilityEnabled(pipelineCapabiltiy));
}
